import React, { type FC } from "react";
import notification from "@app/services/notification";
import { Input } from "@shared/ui/input";
import { BusinessForm } from "@src/components/business-form";
import { FormPanel } from "@shared/ui/form-panel";
import type { WsInvActContent } from "../../../../../../api/wsInvActs";
import { resolveWsInvActContentSearch } from "../../helpers/resolveWsInvActContentSearch";
import { wsInvActCardSearchFormConfig, type WsInvActCardSearchFormValues } from "./wsInvActCardSearch.form-config";

export type WsInvActSearchResolution =
    | { type: "single"; contentCode: number; stockObjSign: string }
    | { type: "ambiguous"; stockObjSign: string; choices: WsInvActContent[] };

/** Пропсы overlay-окна поиска состава АИ. */
export interface WsInvActCardSearchFormProps {
    /** Код акта инвентаризации. */
    actCode: number;
    /** Обработчик результата поиска (закрытие overlay — у opener через `OverlayHandle`). */
    onResolved: (result: WsInvActSearchResolution) => void | Promise<void>;
}

/**
 * Поиск элемента состава АИ (overlay window).
 */
export const WsInvActCardSearchForm: FC<WsInvActCardSearchFormProps> = ({ actCode, onResolved }) => {
    const handleSubmit = async (values: WsInvActCardSearchFormValues): Promise<void> => {
        const stockObjSign = values.stockObjSign.trim();
        const result = await resolveWsInvActContentSearch(actCode, stockObjSign);

        if (result.type === "notFound") {
            notification.warning(
                `Не найдено состава акта инвентаризации с "Обозначением ТМЦ" со значением "${stockObjSign}"`,
            );
            return;
        }

        const resolution: WsInvActSearchResolution =
            result.type === "single"
                ? { type: "single", contentCode: result.contentCode, stockObjSign }
                : { type: "ambiguous", stockObjSign: result.stockObjSign, choices: result.choices };

        await onResolved(resolution);
    };

    return (
        <FormPanel<WsInvActCardSearchFormValues>
            formProps={{
                formConfig: wsInvActCardSearchFormConfig,
                initialValues: { stockObjSign: "" },
                onSubmit: handleSubmit,
            }}
        >
            {(form) => (
                <BusinessForm.Field name="stockObjSign">
                    <Input {...form.field("stockObjSign").getPropsWithLabel()} />
                </BusinessForm.Field>
            )}
        </FormPanel>
    );
};
